import * as React from "react";
import {EvChargingMeterPoint} from "../../api/EvChargingApi";
import {formatDate, formatMprn, MPRN_LENGTH, mprnDigits} from "./evChargingBill";
import {evColors, evMessageStyles} from "./evChargingStyles";

interface Props {
	id: string;
	/**
	 * Digits only; shown grouped 4-3-4.
	 */
	digits: string;
	meterPoints: EvChargingMeterPoint[];
	meterPointsLoading: boolean;
	disabled?: boolean;
	invalid?: boolean;
	/**
	 * Id of the element holding this field's messages.
	 */
	describedBy?: string;
	onChange: (digits: string) => void;
}

interface State {
	open: boolean;
	/**
	 * Option highlighted with the arrow keys, -1 for none.
	 */
	active: number;
}

/**
 * Pick one of the brand's meters, or type a new MPRN. Spaces and dashes are ignored and letters cannot be typed, so
 * what is stored is always the bare 11 digits the backend checks. Works from the keyboard as a combobox: arrows move
 * through the brand's meters, Enter picks one, Escape closes the list.
 */
export default class MprnField extends React.PureComponent<Props, State> {
	state: State = {open: false, active: -1};

	private closeTimer?: number;

	componentWillUnmount() {
		window.clearTimeout(this.closeTimer);
	}

	render() {
		const {id, digits, disabled, invalid, describedBy, meterPoints, meterPointsLoading} = this.props;
		const options = this.options();
		const listId = `${id}-list`;
		const expanded = this.state.open && !disabled && options.length > 0;
		return (
			<div style={{position: "relative"}}>
				<input id={id} className={"a-input"} value={formatMprn(digits)} disabled={disabled}
					   role="combobox" aria-autocomplete="list" aria-expanded={expanded} aria-controls={listId}
					   aria-activedescendant={expanded && this.state.active >= 0 ? `${id}-option-${this.state.active}` : undefined}
					   aria-invalid={invalid} aria-describedby={describedBy}
					   autoComplete="off" inputMode="numeric"
					   placeholder={disabled ? "Choose the partner brand first" : "1001 234 5678"}
					   style={{
						   letterSpacing: ".04em",
						   cursor: disabled ? "not-allowed" : undefined,
						   borderColor: invalid ? evColors.error : undefined
					   }}
					   onFocus={() => this.setState({open: true, active: -1})}
					   onBlur={() => this.closeTimer = window.setTimeout(() => this.setState({open: false}), 150)}
					   onKeyDown={this.onKeyDown}
					   onChange={e => {
						   this.setState({open: true, active: -1});
						   this.props.onChange(mprnDigits(e.target.value));
					   }}/>
				<div style={{...evMessageStyles.hint, display: "flex", justifyContent: "space-between"}}>
					<span>
						{meterPointsLoading ? "Loading this partner brand’s meters…"
							: meterPoints.length ? "Type the 11 digits, or pick one of this partner brand’s meters"
								: disabled ? "11 digits"
									: "This partner brand has no meters yet — type the 11 digits"}
					</span>
					<span>{digits.length} / {MPRN_LENGTH}</span>
				</div>
				{expanded &&
				<ul id={listId} role="listbox" aria-label="Meters of this partner brand" style={styles.list}>
					{options.map((meterPoint, index) =>
						<li key={meterPoint.id} id={`${id}-option-${index}`} role="option"
							aria-selected={index === this.state.active}
							style={{...styles.option, background: index === this.state.active ? evColors.background : "#fff"}}
							onMouseDown={() => this.pick(meterPoint)}>
							<span style={{fontWeight: 600, letterSpacing: ".04em"}}>{formatMprn(meterPoint.mprn)}</span>
							{meterPoint.nickname && <span> · {meterPoint.nickname}</span>}
							<div style={{fontSize: 12, color: evColors.muted}}>
								{meterPoint.billCount} {meterPoint.billCount === 1 ? "job" : "jobs"}
								{meterPoint.lastBillPeriodTo ? ` · last to ${formatDate(meterPoint.lastBillPeriodTo)}` : ""}
							</div>
						</li>)}
				</ul>}
			</div>
		);
	}

	private options(): EvChargingMeterPoint[] {
		return this.props.meterPoints.filter(m => m.mprn.indexOf(this.props.digits) === 0);
	}

	private pick(meterPoint: EvChargingMeterPoint) {
		this.setState({open: false, active: -1});
		this.props.onChange(meterPoint.mprn);
	}

	private onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const options = this.options();
		if (event.key === "ArrowDown" || event.key === "ArrowUp") {
			if (!options.length) return;
			event.preventDefault();
			const step = event.key === "ArrowDown" ? 1 : -1;
			const next = (this.state.active + step + options.length) % options.length;
			this.setState({open: true, active: next});
		}
		else if (event.key === "Enter" && this.state.open && this.state.active >= 0 && options[this.state.active]) {
			// picks the highlighted meter instead of submitting the form
			event.preventDefault();
			this.pick(options[this.state.active]);
		}
		else if (event.key === "Escape" && this.state.open) {
			this.setState({open: false, active: -1});
		}
	};
}

const styles: { [key: string]: React.CSSProperties } = {
	list: {
		position: "absolute", zIndex: 10, left: 0, right: 0, top: 46, margin: 0, padding: 0, listStyle: "none",
		background: "#fff", border: `1px solid ${evColors.border}`, borderRadius: 4,
		boxShadow: "0 4px 12px rgba(17,35,99,0.12)", maxHeight: 260, overflowY: "auto"
	},
	option: {padding: "8px 12px", borderTop: `1px solid ${evColors.background}`, cursor: "pointer", fontSize: 14}
};
